/**
 * Centralized HTTP Client
 * Handles all HTTP requests with automatic X-Tenant-ID injection
 */

import axios from 'axios';
import { TENANT_ID, API_BASE_URL, DEFAULT_API_VERSION } from '@/config/constants';
import { isValidTenantId } from '@/api/endpoints';
import { getImpersonationState } from '@/shared/lib/impersonation';
import { clearAuthStorage, ensureFreshAccessToken, refreshSession } from '@/utils/authSession';
import { handleAdvancedError } from '@/utils/advancedErrorHandler';
import { recordHttpBreadcrumb } from '@/utils/supportClientContext';

let isRefreshing = false;
let failedQueue = [];

const AUTH_SKIP_PATHS = [
  '/auth/login',
  '/auth/super-admin/login',
  '/auth/register',
  '/auth/forget-password',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/verify',
  '/auth/refresh',
];

function isAuthEndpoint(url) {
  if (!url) return false;
  return AUTH_SKIP_PATHS.some((path) => url.includes(path));
}

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const getTenantId = () => {
  const storedTenantId = localStorage.getItem('tenantId');
  if (isValidTenantId(storedTenantId)) {
    return storedTenantId;
  }
  return TENANT_ID;
};

function redirectToLogin() {
  const publicPaths = ['/', '/login', '/privacy', '/delete-account', '/ForgetPassword', '/signup', '/SignUp'];
  const isPublicPage = publicPaths.includes(window.location.pathname);
  if (!isPublicPage) {
    window.location.href = '/login?expired=true';
  }
}

function forceLogout(error) {
  clearAuthStorage();
  redirectToLogin();
  return Promise.reject(error);
}

const httpClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

httpClient.interceptors.request.use(
  async (config) => {
    if (!isAuthEndpoint(config.url)) {
      const token = await ensureFreshAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const tenantId = getTenantId();
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId;
      }
    }

    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = generateDeviceId();
      localStorage.setItem('deviceId', deviceId);
    }
    config.headers['X-Device-ID'] = deviceId;
    config.headers['X-Client-Platform'] = 'web';
    config.headers['X-API-Version'] = String(DEFAULT_API_VERSION);
    config.headers['X-App-Version'] =
      process.env.REACT_APP_APP_VERSION || process.env.REACT_APP_VERSION || 'web';

    const impersonation = getImpersonationState();
    if (impersonation.active && impersonation.sessionId) {
      config.headers['X-Impersonation-Session-Id'] = impersonation.sessionId;
    }

    const language = localStorage.getItem('language') || 'ar';
    config.headers['X-App-Language'] = language;
    config.headers['Accept-Language'] = language;
    config.headers['X-Requested-With'] = 'XMLHttpRequest';

    if (config.baseURL && config.baseURL.includes('ngrok')) {
      config.headers['ngrok-skip-browser-warning'] = 'true';
    }

    config.metadata = { ...(config.metadata || {}), startTime: Date.now() };

    return config;
  },
  (error) => Promise.reject(error)
);

httpClient.interceptors.response.use(
  (response) => {
    const cfg = response.config;
    if (cfg && !String(cfg.url || '').includes('/support/tickets')) {
      const start = cfg.metadata?.startTime;
      recordHttpBreadcrumb({
        method: cfg.method,
        url: cfg.url,
        status: response.status,
        durationMs: typeof start === 'number' ? Date.now() - start : undefined,
        ok: true,
      });
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest && !String(originalRequest.url || '').includes('/support/tickets')) {
      const start = originalRequest.metadata?.startTime;
      recordHttpBreadcrumb({
        method: originalRequest.method,
        url: originalRequest.url,
        status: error.response?.status,
        durationMs: typeof start === 'number' ? Date.now() - start : undefined,
        errorCode:
          error.response?.data?.code ||
          error.response?.data?.errorCode ||
          error.response?.data?.error ||
          undefined,
        ok: false,
      });
    }

    try {
      handleAdvancedError(error, {
        showNotification: false,
        logError: true,
        trackError: true,
        context: {
          url: originalRequest?.url,
          method: originalRequest?.method,
          isRetry: originalRequest._retry,
        },
      });
    } catch (trackingError) {
      console.error('Failed to track HTTP error:', trackingError);
    }

    const code = error.response?.data?.code || error.response?.data?.errorCode;
    if (code === 'ACCOUNT_LOCKED' || code === 'TOKEN_REVOKED') {
      if (code === 'ACCOUNT_LOCKED' && isAuthEndpoint(originalRequest?.url)) {
        return Promise.reject(error);
      }
      if (code === 'TOKEN_REVOKED' && !originalRequest._retry) {
        // fall through to refresh; if refresh also fails we logout below
      } else if (code === 'ACCOUNT_LOCKED') {
        return forceLogout(error);
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint(originalRequest?.url)) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return httpClient(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const nextToken = await refreshSession();

        processQueue(null, nextToken);
        isRefreshing = false;

        originalRequest.headers.Authorization = `Bearer ${nextToken}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        return forceLogout(refreshError);
      }
    }

    if (error.response?.status === 404 &&
      error.response?.data?.error === 'ORGANIZATION_NOT_FOUND') {
      console.warn('⚠️ Organization not found - check tenant ID');
    }

    return Promise.reject(error);
  }
);

function generateDeviceId() {
  // eslint-disable-next-line no-restricted-globals
  const screenObj = typeof screen !== 'undefined' ? screen : { width: 0, height: 0, colorDepth: 0 };
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screenObj.width,
    screenObj.height,
    screenObj.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.platform,
    navigator.hardwareConcurrency || 0,
    navigator.deviceMemory || 0
  ].join('|');

  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return `device_${Math.abs(hash)}_${Date.now()}`;
}

export default httpClient;
